import {
  Box,
  Button,
  Container,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import {DatePicker} from "@mui/x-date-pickers";
import dayjs from "dayjs";
import {useNavigate} from "react-router-dom";
import {Search, X} from "lucide-react";
import {ChangeEvent, useEffect, useMemo, useState} from "react";
import {useGetZyusetuSimpleQuery} from "@/graphql/graphql";
import {useDebounceValue} from "@/hooks/use-debounce-value";
import {UserAutocomplete} from "@/components/input/UserAutocomplete";
import {SimpleZyusetuListView} from "@/components/pages/Zyusetu/SimpleZyusetuListView.tsx";
import {useUserData} from "@/hooks/use-user-data-provider.ts";
import {Permission} from "@/utils/permissions.ts";
import {ZyusetuReviewListPage} from "@/pages/zyusetu/ZyusetuReviewListPage.tsx";
import {ZyusetuTemplateSelector, Template} from "@/components/pages/Zyusetu/ZyusetuTemplateSelector.tsx";

interface User {
  id: string;
  name: string;
  email: string;
  organizationId: string;
}

export function ZyusetuHomePage() {
  const [page, setPage] = useState(1);
  const [searchParams, setSearchParams] = useState({
    title: "",
    createdAtStart: "",
    createdAtEnd: "",
  });
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  
  const debouncedSearchParams = useDebounceValue(searchParams);
  const itemsPerPage = 6;
  
  const handleClearSearch = () => {
    setSearchParams({
      title: "",
      createdAtStart: "",
      createdAtEnd: "",
    });
    setSelectedUser(null);
  };
  
  const userData = useUserData()
  const {data: recentZyusetu, isLoading} = useGetZyusetuSimpleQuery({
    skip: (page - 1) * itemsPerPage,
    take: itemsPerPage,
    search: Object.values(debouncedSearchParams).some(value => value !== "") || selectedUser
      ? {
        title: debouncedSearchParams.title || undefined,
        userId: selectedUser?.id,
        ...(debouncedSearchParams.createdAtStart && {
          createdAtStart: new Date(debouncedSearchParams.createdAtStart)
        }),
        ...(debouncedSearchParams.createdAtEnd && {
          createdAtEnd: new Date(debouncedSearchParams.createdAtEnd)
        })
      }
      : undefined
  });
  
  useEffect(() => {
    setPage(1);
  }, [debouncedSearchParams, selectedUser]);
  
  const handlePageChange = (_event: ChangeEvent<unknown>, value: number) => {
    setPage(value);
    window.scrollTo({top: 0, behavior: 'smooth'});
  };
  const nav = useNavigate();
  const hasWritePermission = useMemo(() => userData.permissions.includes(Permission.WriteZyusetu), [userData.permissions]);
  
  return (<>
      <Container maxWidth="lg">
        {/* 新規作成セクション */}
        <Box sx={{mb: 6}}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{mb: 3}}>
            <Typography variant="pageTitle">新しい重説を作成</Typography>
          </Stack>
          <ZyusetuTemplateSelector
            onSelect={(template: Template) => nav(hasWritePermission ? template.path : "#")}
          />
        </Box>
        
        {/* 重説一覧セクション */}
        <Box sx={{mb: 5}}>
          <SimpleZyusetuListView
            title="重説一覧"
            handlePageChange={handlePageChange}
            page={page}
            zyusetus={recentZyusetu?.getZyusetuSimple ?? {items: [], total: 0}}
            itemsPerPage={itemsPerPage}
            isLoading={isLoading}
          > <Box sx={{mt: 2, mb: 3, p: 3, bgcolor: 'background.paper', borderRadius: 2, boxShadow: 1}}>
            <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2}}>
              <Box>
                <Typography variant="subtitle1" fontWeight="bold">重説を検索</Typography>
              </Box>
              {(Object.values(searchParams).some(value => value !== "") || selectedUser) && (
                <Button
                  variant="contained"
                  color="error"
                  size="small"
                  onClick={handleClearSearch}
                  startIcon={<X size={18}/>}
                  sx={{ml: 2}}
                >
                  検索条件をクリア
                </Button>
              )}
            </Box>
            <Stack spacing={3}>
              <Stack direction="row" spacing={2}>
                <Box sx={{flex: 1}}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="重説のタイトルを入力"
                    value={searchParams.title}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchParams(prev => ({
                      ...prev,
                      title: e.target.value
                    }))}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search size={20}/>
                        </InputAdornment>
                      )
                    }}
                  />
                </Box>
                <Box sx={{flex: 1}}>
                  <Tooltip title={
                    !userData.permissions.includes(Permission.ReadOtherZyusetu) ? "権限がありません" : ""
                  }>
                  <span>
                  
                  <UserAutocomplete
                    disabled={!userData.permissions.includes(Permission.ReadOtherZyusetu)}
                    value={selectedUser}
                    onChange={setSelectedUser}
                    placeholder="作成者の名前を入力"
                  />
                  </span>
                  </Tooltip>
                </Box>
              </Stack>
              
              <Stack direction="row" spacing={2}>
                <Box sx={{flex: 1}}>
                  <DatePicker
                    label="作成日（開始）"
                    format="YYYY年MM月DD日"
                    value={searchParams.createdAtStart ? dayjs(searchParams.createdAtStart) : null}
                    onChange={(value) => {
                      setSearchParams(prev => ({
                        ...prev,
                        createdAtStart: value ? value.format('YYYY-MM-DD') : ''
                      }));
                    }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: "small",
                        placeholder: "開始日を選択",
                        inputProps: {
                          "aria-label": "作成日（開始）"
                        }
                      },
                      calendarHeader: {
                        format: "YYYY年MM月"
                      },
                      openPickerButton: {
                        size: "small"
                      }
                    }}
                    closeOnSelect
                  />
                </Box>
                <Box sx={{flex: 1}}>
                  <DatePicker
                    label="作成日（終了）"
                    format="YYYY年MM月DD日"
                    value={searchParams.createdAtEnd ? dayjs(searchParams.createdAtEnd) : null}
                    onChange={(value) => {
                      setSearchParams(prev => ({
                        ...prev,
                        createdAtEnd: value ? value.format('YYYY-MM-DD') : ''
                      }));
                    }}
                    minDate={searchParams.createdAtStart ? dayjs(searchParams.createdAtStart) : undefined}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: "small",
                        placeholder: "終了日を選択",
                        inputProps: {
                          "aria-label": "作成日（終了）"
                        }
                      },
                      calendarHeader: {
                        format: "YYYY年MM月"
                      },
                      openPickerButton: {
                        size: "small"
                      }
                    }}
                    closeOnSelect
                  />
                </Box>
              </Stack>
            </Stack>
          </Box></SimpleZyusetuListView>
        </Box>
      </Container>
      {
        userData.permissions.includes(Permission.WriteApprove) && userData?.user?.organization?.useApprovalFlow &&
        <ZyusetuReviewListPage/>
      }
    </>
  );
}
